import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PHONE_MOVIES } from "../data/curated";
import { ScrollText } from "./ScrollText";
import { useRailLoop } from "./useRailLoop";

const THUMB_W = 88; // px — thumbnails stay this size, phone takes the rest
const VISIBLE = 4; // thumbnail slots shown at once
const CAPTION_H = 14; // movie title under each thumb (title only)
const SLOT_H = THUMB_W * 1.5 + CAPTION_H;

export default function PhoneVideoSection() {
    const [idx, setIdx] = useState(0);
    const vidRef = useRef(null);
    const film = PHONE_MOVIES[idx];
    const [ended, setEnded] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const frameRef = useRef(null);
    const [railH, setRailH] = useState(null);
    const [resolvedSrc, setResolvedSrc] = useState(null);

    // Resolve streamable:// slugs to a real MP4 URL via the public API
    useEffect(() => {
        if (!film.src) { setResolvedSrc(null); return; }
        if (!film.src.startsWith("streamable://")) {
            setResolvedSrc(film.src);
            return;
        }
        const id = film.src.replace("streamable://", "");
        setResolvedSrc(null);
        fetch(`https://api.streamable.com/videos/${id}`)
            .then((r) => r.json())
            .then((d) => {
                const url =
                    d?.files?.mp4?.url ||
                    d?.files?.["mp4-mobile"]?.url ||
                    null;
                setResolvedSrc(url ? (url.startsWith("//") ? "https:" + url : url) : null);
            })
            .catch(() => setResolvedSrc(null));
    }, [film.src]);

    const loop = PHONE_MOVIES.length > VISIBLE;
    const visibleCount = Math.min(PHONE_MOVIES.length, VISIBLE);
    const strip = loop ? [...PHONE_MOVIES, ...PHONE_MOVIES] : PHONE_MOVIES;
    const railRef = useRef(null);
    useRailLoop(railRef, loop, PHONE_MOVIES.length * 4);

    // Stretch the rail window to run flush with the phone top and bottom
    // (thumbnail size stays fixed; only the spacing flexes). All widths —
    // the desktop layout reuses this phone mockup side by side.
    useLayoutEffect(() => {
        const measure = () => {
            const h = frameRef.current?.getBoundingClientRect().height ?? 0;
            setRailH(
                h >= VISIBLE * SLOT_H + (VISIBLE - 1) * 6
                    ? Math.round(h) - 35
                    : null
            );
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);
    // Stacked-band (640-767px) thumbs sit 20px apart; elsewhere tight as-is
    const stackedBand =
        window.matchMedia("(min-width: 640px) and (max-width: 767px)")
            .matches;
    const railGap = railH
        ? Math.min(
              (railH - VISIBLE * SLOT_H) / (VISIBLE - 1),
              stackedBand ? 5 : 0
          )
        : 10;

    // Preload every movie's phone art so switching is instant (no pop-in)
    useEffect(() => {
        PHONE_MOVIES.forEach((m) => {
            const i = new Image();
            i.src = m.phoneArt;
        });
    }, []);

    // Selecting a thumbnail only BROWSES: it swaps the phone screen and
    // reveals that movie's info — it never auto-plays the trailer and
    // never interrupts a playing song. Playback is the Play pill's job.
    // The artwork stays visible until playback actually starts — an idle
    // <video> renders as a black layer on mobile Safari and its scaled
    // edge shows a hairline seam, so it's faded in only on `onPlaying`.
    useEffect(() => {
        setEnded(false);
        setShowVideo(false);
        setIsPlaying(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx]);

    // Pause this movie when a song starts playing anywhere on the page
    useEffect(() => {
        const pauseOnAudio = (e) => {
            if (e.detail?.source === "audio") {
                const v = vidRef.current;
                if (v && !v.paused) v.pause();
            }
        };
        window.addEventListener("rivvr:media-play", pauseOnAudio);
        return () =>
            window.removeEventListener("rivvr:media-play", pauseOnAudio);
    }, []);

    const togglePlay = () => {
        const v = vidRef.current;
        if (!v) return;
        if (ended) {
            v.currentTime = 0;
            v.play().catch(() => {});
            setEnded(false);
            return;
        }
        if (v.paused) v.play().catch(() => {});
        else v.pause();
    };

    return (
        <div
            className="overflow-x-clip px-4"
            data-testid="phone-video-section"
        >
            <div className="-mx-4 mx-auto flex max-w-[430px] flex-col md:[zoom:0.819] lg:[zoom:0.9391] xl:[zoom:1.0156] 2xl:[zoom:1.05]">
                <div
                    className="relative z-10 -mb-2 -translate-y-[23px] max-sm:-translate-x-[51px] flex flex-col items-start gap-1 sm:mb-4 sm:translate-x-[1px] sm:-translate-y-[6px] md:mb-6 md:mx-auto md:w-[calc(100%-96px)] md:-translate-y-0 md:-translate-x-[45px]"
                    data-testid="video-header"
                >
                    <h3 className="shrink-0 max-sm:translate-x-[98px] sm:translate-x-[50px] font-display text-[clamp(1.5rem,6vw,2rem)] font-bold leading-tight tracking-tight text-ivory md:text-[clamp(1.5rem,6vw,34px)]">
                        <span className="text-neon">RIVVR</span>{" "}
                        <span className="italic">Streams</span>
                    </h3>
                    <div className="flex flex-col gap-0 text-[16px] md:text-[17px] lg:text-[17px] xl:text-[16px] 2xl:text-[17px] leading-[1.2] text-[#8CC8FF] -translate-y-[4px] max-sm:translate-x-[75px] sm:translate-x-[46px]">
                        <span className="whitespace-nowrap">• RIVVR Original Movies &amp; TV •</span>
                        <span className="whitespace-nowrap sm:-translate-x-[22px] max-sm:-translate-x-[20px]">• New &amp; Classic Shows to be seen •</span>
                    </div>
                </div>
                <div className="-mt-[20px] max-sm:-mt-[30px] flex items-center gap-2">
                <div className="relative min-w-0 flex-1 max-sm:scale-[0.94] max-sm:-translate-x-1">
                    <div
                        ref={frameRef}
                        className="relative mx-auto w-full rounded-[2.6rem] border border-white/10 bg-coal p-[7px] shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9),0_0_70px_-20px_rgba(3,135,226,0.25)]"
                    >
                        <div
                            className="relative aspect-[9/19.95] md:aspect-[9/19.2] overflow-hidden rounded-[2.1rem] bg-void [-webkit-mask-image:radial-gradient(#fff,#fff)] [mask-image:radial-gradient(#fff,#fff)]"
                            data-testid="movie-screen"
                        >
                            <img
                                src={film.phoneArt}
                                alt={film.title}
                                data-testid="movie-screen-art"
                                className="absolute inset-0 h-full w-full scale-[1.01] rounded-[2.1rem] object-cover"
                            />
                            {resolvedSrc && (
                                <>
                                    <video
                                        key={film.id}
                                        ref={vidRef}
                                        data-testid="movie-video"
                                        src={resolvedSrc}
                                        poster={film.phoneArt}
                                        playsInline
                                        preload="none"
                                        className={`absolute inset-0 h-full w-full scale-[1.01] rounded-[2.1rem] object-cover transition-opacity duration-700 ${
                                            showVideo && !ended
                                                ? "opacity-100"
                                                : "opacity-0"
                                        }`}
                                        onPlaying={() => setShowVideo(true)}
                                        onEnded={() => {
                                            setEnded(true);
                                            setShowVideo(false);
                                        }}
                                        onClick={togglePlay}
                                        onPlay={() => {
                                            setIsPlaying(true);
                                            window.dispatchEvent(
                                                new CustomEvent(
                                                    "rivvr:media-play",
                                                    {
                                                        detail: {
                                                            source: "video",
                                                        },
                                                    }
                                                )
                                            );
                                        }}
                                        onPause={() => setIsPlaying(false)}
                                    />
                                    <button
                                        data-testid="movie-play-btn"
                                        aria-label={
                                            isPlaying
                                                ? "Pause movie"
                                                : "Play movie"
                                        }
                                        onClick={togglePlay}
                                        className={`absolute left-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-full border py-2 pl-3.5 pr-4 backdrop-blur-md transition-all duration-300 hover:scale-105 active:scale-95 ${
                                            isPlaying
                                                ? "border-neon bg-neon text-white"
                                                : "animate-play-pill border-white/30 bg-black/45 text-white hover:border-white/70"
                                        }`}
                                        style={{
                                            top: "68.2%",
                                            "--pulse-rgb": "255, 255, 255",
                                        }}
                                    >
                                        {isPlaying ? (
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                                className="h-3.5 w-3.5"
                                            >
                                                <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
                                            </svg>
                                        ) : (
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                                className="h-3.5 w-3.5"
                                            >
                                                <path d="M8 5.14v13.72L19 12 8 5.14z" />
                                            </svg>
                                        )}
                                        <span className="text-[11px] font-semibold uppercase tracking-wide">
                                            {isPlaying ? "Pause" : "Play"}
                                        </span>
                                    </button>
                                </>
                            )}
                            <div className="pointer-events-none absolute left-1/2 top-2.5 z-10 h-[15px] w-[62px] -translate-x-1/2 rounded-full bg-black" />
                        </div>
                    </div>
                </div>
                <div
                    className="shrink-0 overflow-hidden"
                    style={{
                        width: THUMB_W,
                        height:
                            railH ??
                            visibleCount * THUMB_W * 1.5 + (visibleCount - 1) * 10,
                        contain: "paint",
                    }}
                    data-testid="movie-thumb-window"
                >
                    <div
                        ref={railRef}
                        className={`flex w-full flex-col ${
                            loop ? "thumbloop-rail" : "justify-between"
                        }`}
                        style={{
                            gap: railGap,
                            paddingBottom: loop ? railGap : 0,
                        }}
                    >
                        {strip.map((m, i) => {
                            const movieIdx = i % PHONE_MOVIES.length;
                            const isDup = i >= PHONE_MOVIES.length;
                            return (
                                <div
                                    key={`${m.id}-${isDup ? "b" : "a"}`}
                                    className="w-full"
                                >
                                    <button
                                        data-testid={`movie-thumb-btn-${movieIdx}`}
                                        onClick={() => setIdx(movieIdx)}
                                        aria-label={`Play ${m.title}`}
                                        aria-hidden={isDup}
                                        tabIndex={isDup ? -1 : 0}
                                        className={`aspect-[2/3] w-full overflow-hidden rounded-xl border transition-[opacity,border-color,box-shadow] duration-300 ${
                                            movieIdx === idx
                                                ? "border-neon shadow-[0_0_18px_rgba(3,135,226,0.45)]"
                                                : "border-white/10 opacity-80 hover:opacity-100"
                                        }`}
                                    >
                                        <img
                                            src={m.thumb}
                                            alt={m.title}
                                            decoding="async"
                                            draggable={false}
                                            className="block h-full w-full object-cover"
                                        />
                                    </button>
                                    <div className="-mt-1 h-[26px] overflow-hidden text-center leading-[1.1]">
                                        <ScrollText
                                            text={m.title}
                                            moving={true}
                                            className={`text-[12px] font-semibold ${
                                                movieIdx === idx
                                                    ? "text-white"
                                                    : "text-white/70"
                                            }`}
                                        />
                                        {m.label && (
                                            <p className={`-mt-[2px] text-[12px] leading-[1.2] truncate ${
                                                movieIdx === idx
                                                    ? "text-[#8CC8FF]"
                                                    : "text-white/40"
                                            }`}>
                                                {m.label}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                </div>
                <p
                    className="relative z-20 transform-gpu sm:hidden max-sm:mt-[-13px] max-sm:ml-[69px] text-[15px] font-bold uppercase leading-snug tracking-wide text-[#8CC8FF]"
                    data-testid="video-available-note"
                >
                    Launching Q2 2027
                </p>
                <p
                    className="hidden sm:block text-center -translate-x-[45px] -translate-y-[4px] mt-3 text-[15px] font-bold uppercase tracking-wide text-[#8CC8FF]"
                    data-testid="video-available-note-desktop"
                >
                    Launching Q2 2027
                </p>
                <p
                    className="-mt-[2px] max-sm:w-full max-sm:translate-x-0 max-sm:ml-0 max-sm:text-left sm:w-[calc(100%+10px)] sm:-translate-x-[20px] sm:text-left text-[16.5px] leading-snug text-white"
                    data-testid="video-streams-cta"
                >
                    Coming Soon, RIVVR Movies &amp; TV. Watch New and Classic Movies, Series and Live TV shows. Exclusive RIVVR Originals only seen here.
                </p>
            </div>
        </div>
    );
}
